import apiClient from './api'
import type {
  UserProfile,
  WalletInfo,
  OrderRecord,
  TransactionRecord,
  ChatMessage,
  ChatSession,
  PasswordChangeData,
  BankAccountInfo,
  RechargeGuide
} from '@/types/user'
import type {
  ExtendedUserProfile,
  MembershipLevel,
  SystemNotification,
  ProjectStatistics,
  PaymentAddress,
  TransactionSummary
} from '@/types/personal-center'
import { ErrorHandler } from '@/utils/errorHandler'

export class UserService {
  /**
   * 包装API调用，添加统一的错误处理
   */
  private static async withErrorHandling<T>(
    operation: () => Promise<T>,
    context: string = 'UserService'
  ): Promise<T> {
    try {
      return await operation()
    } catch (error) {
      // 记录错误上下文
      ErrorHandler.logError(
        ErrorHandler.fromAxiosError(error),
        context
      )
      throw error
    }
  }

  // 获取用户个人资料
  static async getProfile(): Promise<UserProfile> {
    return this.withErrorHandling(async () => {
      try {
        console.log('[UserService] 请求用户资料...')
        const response = await apiClient.get<{ user: UserProfile }>('/auth/me')
        console.log('[UserService] 用户资料响应:', response)
        localStorage.setItem('auth_user',JSON.stringify((response as { data: { user: UserProfile } }).data.user));
        return (response as { data: { user: UserProfile } }).data.user
      } catch (error) {
        console.error('[UserService] 获取用户资料失败:', error)
        // 如果认证失败，尝试使用/user端点
        try {
          const userResponse = await apiClient.get('/user')
          console.log('[UserService] 备用用户资料响应:', userResponse)
          return (userResponse as any).data
        } catch (fallbackError) {
          console.error('[UserService] 备用用户资料也失败:', fallbackError)
          throw error
        }
      }
    }, 'getProfile')
  }

  // 更新用户头像
  static async updateAvatar(file: File): Promise<string> {
    return this.withErrorHandling(async () => {
      const formData = new FormData()
      formData.append('avatar', file)

      const response = await apiClient.post('/user/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return (response as any).data.data.avatar_url
    }, 'updateAvatar')
  }

  // 获取钱包信息 - 使用余额API替代
  static async getWalletInfo(): Promise<WalletInfo> {
    return this.withErrorHandling(async () => {
      const response = await apiClient.get('/balance')

      if (response.success && (response as any).data) {
        const rawData = (response as any).data

        // 确保数据类型正确转换
        const walletData: WalletInfo = {
          total_balance: typeof rawData.balance === 'string' ? parseFloat(rawData.balance) : rawData.balance,
          available_balance: typeof rawData.balance === 'string' ? parseFloat(rawData.balance) : rawData.balance,
          carbon_credits: typeof rawData.carbon_credits === 'string' ? parseFloat(rawData.carbon_credits) : rawData.carbon_credits,
          frozen_balance: 0 // 暂时设为0，因为余额API没有冻结余额字段
        }

        return walletData
      } else {
        throw ErrorHandler.createError('SERVER_ERROR', '钱包信息API响应格式异常')
      }
    }, 'getWalletInfo')
  }

  // 获取订单记录
  static async getOrderRecords(page = 1, limit = 20): Promise<{
    orders: OrderRecord[]
    total: number
    current_page: number
    last_page: number
  }> {
    const response = await apiClient.get('/user/orders', {
      params: { page, limit }
    })
    return (response as any).data.data
  }

  // 获取交易历史
  static async getTransactionHistory(page = 1, limit = 20): Promise<{
    transactions: TransactionRecord[]
    total: number
    current_page: number
    last_page: number
  }> {
    const response = await apiClient.get('/user/transactions', {
      params: { page, limit }
    })
    return (response as any).data.data
  }

  // 修改密码
  static async changePassword(data: PasswordChangeData): Promise<void> {
    await apiClient.post('/user/change-password', data)
  }

  // 修改登录密码（新方法，用于账号设定页面）
  static async changeLoginPassword(data: {
    new_password: string
    new_password_confirmation: string
  }): Promise<void> {
    return this.withErrorHandling(async () => {
      await apiClient.post('/user/change-login-password', data)
    }, 'changeLoginPassword')
  }

  // 更新银行账户信息
  static async updateBankAccount(data: BankAccountInfo): Promise<void> {
    await apiClient.post('/user/bank-account', data)
  }

  // 获取银行账户信息
  static async getBankAccount(): Promise<BankAccountInfo> {
    const response = await apiClient.get('/user/bank-account')
    return (response as any).data.data.bank_account
  }

  // 获取聊天会话
  static async getChatSession(): Promise<ChatSession> {
    const response = await apiClient.get('/user/chat/session')
    return (response as any).data.data.session
  }

  // 获取聊天消息
  static async getChatMessages(sessionId: number, page = 1): Promise<{
    messages: ChatMessage[]
    total: number
    current_page: number
    last_page: number
  }> {
    const response = await apiClient.get(`/user/chat/sessions/${sessionId}/messages`, {
      params: { page }
    })
    return (response as any).data.data
  }

  // 发送文字消息
  static async sendTextMessage(sessionId: number, message: string): Promise<ChatMessage> {
    const response = await apiClient.post(`/user/chat/sessions/${sessionId}/messages`, {
      message,
      message_type: 'text'
    })
    return (response as any).data.data.message
  }

  // 发送图片消息
  static async sendImageMessage(sessionId: number, file: File): Promise<ChatMessage> {
    const formData = new FormData()
    formData.append('image', file)
    formData.append('message_type', 'image')

    const response = await apiClient.post(`/user/chat/sessions/${sessionId}/messages`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return (response as any).data.data.message
  }

  // 标记消息为已读
  static async markMessagesAsRead(sessionId: number): Promise<void> {
    await apiClient.post(`/user/chat/sessions/${sessionId}/read`)
  }

  // 获取充值指南
  static async getRechargeGuide(): Promise<RechargeGuide> {
    const response = await apiClient.get('/user/recharge/guide')
    return (response as any).data.data.guide
  }

  // 创建充值申请
  static async createRechargeRequest(data: {
    amount: number
    payment_method_id: number
    payment_proof?: File
    note?: string
  }): Promise<{ request_id: number }> {
    const formData = new FormData()
    formData.append('amount', data.amount.toString())
    formData.append('payment_method_id', data.payment_method_id.toString())
    if (data.payment_proof) {
      formData.append('payment_proof', data.payment_proof)
    }
    if (data.note) {
      formData.append('note', data.note)
    }

    const response = await apiClient.post('/user/recharge/request', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return (response as any).data.data
  }

  // ===== 个人中心重新设计 - 新增API方法 =====

  // 获取扩展用户资料
  static async getExtendedProfile(): Promise<ExtendedUserProfile> {
    return this.withErrorHandling(async () => {
      console.log('[UserService] 请求扩展用户资料...')
      try {
        const response = await apiClient.get('/user/profile/extended')
        console.log('[UserService] 扩展用户资料响应:', response)
        return (response as any).data.user
      } catch (error) {
        console.warn('[UserService] 扩展用户资料API不存在，使用基础资料数据')
        // 如果扩展资料API不存在，尝试从基础资料构建
        const basicProfile = await this.getProfile()
        return {
          id: basicProfile.id,
          email: basicProfile.email,
          first_name: basicProfile.first_name,
          last_name: basicProfile.last_name,
          phone: basicProfile.phone || '',
          license_passport: '',
          account_frozen: false,
          withdraw_pin_set: false,
          failed_withdraw_attempts: 0,
          created_at: basicProfile.created_at || new Date().toISOString(),
          updated_at: basicProfile.updated_at || new Date().toISOString()
        }
      }
    }, 'getExtendedProfile')
  }

  // 获取会员等级信息
  static async getMembershipInfo(): Promise<{
    current_level: MembershipLevel
    next_level?: MembershipLevel
    progress_to_next: number
  }> {
    return this.withErrorHandling(async () => {
      try {
        const response = await apiClient.get('/user/membership')
        return (response as any).data
      } catch (error) {
        console.warn('[UserService] 会员信息API不存在，使用默认数据')
        // 返回默认的会员信息
        return {
          current_level: {
            level: 'P1',
            title: 'Bronze Member',
            requirements: {
              min_investment: 0,
              min_carbon_credits: 0
            }
          },
          next_level: {
            level: 'P2',
            title: 'Silver Member',
            requirements: {
              min_investment: 10000,
              min_carbon_credits: 100
            }
          },
          progress_to_next: 0
        }
      }
    }, 'getMembershipInfo')
  }

  // 获取系统通知
  static async getSystemNotifications(page = 1, limit = 20): Promise<{
    notifications: SystemNotification[]
    unread_count: number
    total: number
    current_page: number
    last_page: number
  }> {
    return this.withErrorHandling(async () => {
      try {
        const response = await apiClient.get('/user/notifications', {
          params: { page, limit }
        })
        return (response as any).data
      } catch (error) {
        console.warn('[UserService] 系统通知API不存在，返回空数据')
        // 返回空的通知数据
        return {
          notifications: [],
          unread_count: 0,
          total: 0,
          current_page: 1,
          last_page: 1
        }
      }
    }, 'getSystemNotifications')
  }

  // 标记通知为已读
  static async markNotificationAsRead(notificationId: number): Promise<void> {
    await apiClient.post(`/user/notifications/${notificationId}/read`)
  }

  // 标记所有通知为已读
  static async markAllNotificationsAsRead(): Promise<void> {
    await apiClient.post('/user/notifications/read-all')
  }

  // 获取项目统计
  static async getProjectStatistics(): Promise<ProjectStatistics> {
    return this.withErrorHandling(async () => {
      console.log('[UserService] 请求项目统计数据...')
      console.log('[UserService] API URL: /user/projects/statistics')

      try {
        const response = await apiClient.get('/user/projects/statistics')
        console.log('[UserService] 项目统计API成功响应:', response)

        if (response.success && (response as any).data) {
          const data = (response as any).data

          // 确保碳积分数据正确映射
          if (data.regular_projects) {
            data.regular_projects = data.regular_projects.map((project: any) => ({
              ...project,
              carbon_credits_earned: project.carbon_credits_earned || project.carbon_credits || 0
            }))
          }

          if (data.presale_projects) {
            data.presale_projects = data.presale_projects.map((project: any) => ({
              ...project,
              carbon_credits_earned: project.carbon_credits_earned || project.carbon_credits || 0
            }))
          }

          return data
        } else {
          console.warn('[UserService] API响应格式异常:', response)
          throw new Error('API响应格式异常')
        }
      } catch (error) {
        console.error('[UserService] 项目统计API调用失败:', error)
        throw error
      }
    }, 'getProjectStatistics')
  }

  // 获取项目统计（备用方法，包含模拟数据）
  static async getProjectStatisticsWithFallback(): Promise<ProjectStatistics> {
    try {
      const response = await apiClient.get('/user/projects/statistics')
      return (response as any).data.data
    } catch (error) {
      // 如果API不存在，返回模拟数据用于测试
      console.warn('项目统计API不存在，使用模拟数据')
      return {
        total_purchased: 15000,
        total_sold: 8500,
        total_carbon_credits: 125.5,
        active_projects_count: 3,
        completed_projects_count: 2,
        regular_projects: [
          {
            id: 1,
            name: 'Solar Energy Project Alpha',
            type: 'regular',
            status: 'ongoing',
            investment_amount: 5000,
            carbon_credits_earned: 45.2,
            progress_percentage: 75,
            project_image_url: '',
            expected_completion_date: '2025-03-15',
            created_at: '2024-10-01T00:00:00Z',
            updated_at: '2024-12-01T00:00:00Z'
          },
          {
            id: 2,
            name: 'Wind Farm Project Beta',
            type: 'regular',
            status: 'completed',
            investment_amount: 7500,
            carbon_credits_earned: 68.3,
            progress_percentage: 100,
            project_image_url: '',
            expected_completion_date: '2024-11-30',
            created_at: '2024-08-15T00:00:00Z',
            updated_at: '2024-11-30T00:00:00Z'
          },
          {
            id: 3,
            name: 'Reforestation Initiative Gamma',
            type: 'regular',
            status: 'ongoing',
            investment_amount: 2500,
            carbon_credits_earned: 12.0,
            progress_percentage: 45,
            project_image_url: '',
            expected_completion_date: '2025-06-30',
            created_at: '2024-11-01T00:00:00Z',
            updated_at: '2024-12-15T00:00:00Z'
          }
        ],
        presale_projects: [
          {
            id: 4,
            name: 'Ocean Carbon Capture Delta',
            type: 'presale',
            status: 'pending_payment',
            investment_amount: 3000,
            carbon_credits_earned: 0,
            progress_percentage: 25,
            final_payment_due: '2025-01-15',
            final_payment_amount: 2000,
            project_image_url: '',
            expected_completion_date: '2025-08-30',
            created_at: '2024-12-01T00:00:00Z',
            updated_at: '2024-12-20T00:00:00Z'
          },
          {
            id: 5,
            name: 'Biomass Energy Project Epsilon',
            type: 'presale',
            status: 'ongoing',
            investment_amount: 4500,
            carbon_credits_earned: 0,
            progress_percentage: 60,
            final_payment_due: '2024-12-30',
            final_payment_amount: 1500,
            project_image_url: '',
            expected_completion_date: '2025-04-15',
            created_at: '2024-09-15T00:00:00Z',
            updated_at: '2024-12-10T00:00:00Z'
          }
        ],
        total_earnings: 3250,
        monthly_earnings: [
          { month: '2024-08', earnings: 450 },
          { month: '2024-09', earnings: 680 },
          { month: '2024-10', earnings: 520 },
          { month: '2024-11', earnings: 890 },
          { month: '2024-12', earnings: 710 }
        ]
      }
    }
  }

  // 获取收款地址列表
  static async getPaymentAddresses(): Promise<PaymentAddress[]> {
    return this.withErrorHandling(async () => {
      try {
        console.log('[UserService] 请求收款地址列表...')
        const response = await apiClient.get('/user/payment-addresses')
        console.log('[UserService] 收款地址响应:', response)
        return (response as any).data.addresses
      } catch (error) {
        console.warn('[UserService] 收款地址API不存在，返回空数组')
        return []
      }
    }, 'getPaymentAddresses')
  }

  // 添加收款地址
  static async addPaymentAddress(data: {
    type: 'bank' | 'btc' | 'eth' | 'sol' | 'bnb' | 'trc'
    label: string
    address: string
  }): Promise<PaymentAddress> {
    return this.withErrorHandling(async () => {
      const response = await apiClient.post('/user/payment-addresses', data)
      return (response as any).data.address
    }, 'addPaymentAddress')
  }

  // 更新收款地址（支持修改鏈類型）
  static async updatePaymentAddress(addressId: number, data: {
    type?: 'bank' | 'btc' | 'eth' | 'sol' | 'bnb' | 'trc'
    label: string
    address: string
  }): Promise<PaymentAddress> {
    return this.withErrorHandling(async () => {
      const response = await apiClient.put(`/user/payment-addresses/${addressId}`, data)
      return (response as any).data.address
    }, 'updatePaymentAddress')
  }

  // 删除收款地址
  static async deletePaymentAddress(addressId: number): Promise<void> {
    return this.withErrorHandling(async () => {
      await apiClient.delete(`/user/payment-addresses/${addressId}`)
    }, 'deletePaymentAddress')
  }

  // 设置提现密码
  static async setWithdrawPin(pin: string): Promise<void> {
    return this.withErrorHandling(async () => {
      await apiClient.post('/user/withdraw-pin', { pin })
    }, 'setWithdrawPin')
  }

  // 验证提现密码
  static async verifyWithdrawPin(pin: string): Promise<{
    valid: boolean
    remaining_attempts?: number
    account_locked?: boolean
  }> {
    return this.withErrorHandling(async () => {
      const response = await apiClient.post('/user/withdraw-pin/verify', { pin })
      return (response as any).data
    }, 'verifyWithdrawPin')
  }

  // 验证登录密码
  static async verifyPassword(password: string): Promise<{
    valid: boolean
    remaining_attempts?: number
    account_locked?: boolean
  }> {
    return this.withErrorHandling(async () => {
      const response = await apiClient.post('/user/password/verify', { password })
      return (response as any).data
    }, 'verifyPassword')
  }

  // 重置提现密码错误次数
  static async resetWithdrawAttempts(): Promise<void> {
    return this.withErrorHandling(async () => {
      await apiClient.post('/user/withdraw-pin/reset-attempts')
    }, 'resetWithdrawAttempts')
  }

  // 创建提现申请
  static async createWithdrawRequest(data: {
    amount: number
    address_id: number
    withdraw_pin: string
  }): Promise<{ request_id: number, fee: number }> {
    return this.withErrorHandling(async () => {
      const response = await apiClient.post('/user/withdraw/request', data)
      return (response as any).data
    }, 'createWithdrawRequest')
  }

  // 获取转账历史摘要
  static async getTransactionSummary(): Promise<TransactionSummary> {
    return this.withErrorHandling(async () => {
      const response = await apiClient.get('/user/transactions/summary')
      return (response as any).data
    }, 'getTransactionSummary')
  }

  // 更新用户个人信息
  static async updateProfile(data: {
    license_passport?: string
    first_name?: string
    last_name?: string
  }): Promise<ExtendedUserProfile> {
    return this.withErrorHandling(async () => {
      const response = await apiClient.put('/user/profile', data)
      return (response as any).data.user
    }, 'updateProfile')
  }

  // ===== 资产管理相关API方法 =====

  // 获取资产摘要
  static async getAssetSummary(): Promise<any> {
    return this.withErrorHandling(async () => {
      console.log('[UserService] 请求资产摘要...')
      const response = await apiClient.get('/assets/summary')
      console.log('[UserService] 资产摘要响应:', response)

      if (response.success && (response as any).data) {
        return (response as any).data
      } else {
        throw new Error('资产摘要API响应格式异常')
      }
    }, 'getAssetSummary')
  }

  // 获取资产明细分解
  static async getAssetBreakdown(): Promise<any[]> {
    return this.withErrorHandling(async () => {
      console.log('[UserService] 请求资产明细分解...')
      const response = await apiClient.get('/assets/breakdown')
      console.log('[UserService] 资产明细响应:', response)

      if (response.success && (response as any).data) {
        return (response as any).data.sources || []
      } else {
        throw new Error('资产明细API响应格式异常')
      }
    }, 'getAssetBreakdown')
  }

  // 获取资产变化历史
  static async getAssetHistory(timeRange = '30d'): Promise<any[]> {
    return this.withErrorHandling(async () => {
      console.log('[UserService] 请求资产变化历史...', { timeRange })

      // 将时间范围转换为天数
      const daysMap: { [key: string]: number } = {
        '7d': 7,
        '30d': 30,
        '90d': 90,
        '1y': 365
      }
      const days = daysMap[timeRange] || 30

      const response = await apiClient.get('/balance/asset-history', {
        params: { days }
      })
      console.log('[UserService] 资产历史响应:', response)

      if (response.success && (response as any).data) {
        return (response as any).data || []
      } else {
        throw new Error('资产历史API响应格式异常')
      }
    }, 'getAssetHistory')
  }

  // 获取提款订单列表
  static async getWithdrawRequests(page = 1): Promise<{
    data: Array<{
      id: number
      user_id: number
      amount: string
      fee: string
      net_amount: string
      address_id: number
      status: 'pending' | 'completed' | 'rejected' | 'processing' | 'cancelled'
      reason: string | null
      admin_notes: string | null
      processed_by: number | null
      processed_at: string | null
      transaction_hash: string | null
      reference_number: string | null
      created_at: string
      updated_at: string
    }>
    current_page: number
    last_page: number
    total: number
  }> {
    return this.withErrorHandling(async () => {
      const response = await apiClient.get('/user/withdraw/requests', {
        params: { page }
      })
      return (response as any).data
    }, 'getWithdrawRequests')
  }

  // 计算售卖影响
  static async calculateSaleImpact(saleQuantity: number, buyerPrice: number): Promise<any> {
    return this.withErrorHandling(async () => {
      console.log('[UserService] 请求计算售卖影响...', { saleQuantity, buyerPrice })
      const response = await apiClient.post('/assets/calculate-sale-impact', {
        sale_quantity: saleQuantity,
        buyer_price: buyerPrice
      })
      console.log('[UserService] 售卖影响响应:', response)

      if (response.success && (response as any).data) {
        return (response as any).data
      } else {
        throw new Error('售卖影响计算API响应格式异常')
      }
    }, 'calculateSaleImpact')
  }

}

export default UserService